// Pure helpers for the LLD 12 device-scope predicate builder.
//
// These are deliberately UI-framework-free so RuleFormPage and its vitest
// suite can share one source of truth (the test env is `node` with no DOM /
// no testing-library, so the form's logic is exercised through these
// functions, mirroring the existing RuleFormPage.test.tsx convention).

import type {
  DeviceScope,
  MatchCondition,
  MatchJoin,
  MatchOp,
  ScopeMode,
} from "@/types/rule";
import {
  CONTAINS_OVERMATCH_MIN_LENGTH,
  MATCH_VALUE_MAX_LENGTH,
  MAX_MATCH_CONDITIONS_PER_AXIS,
} from "@/types/rule";

/**
 * The FIVE device-scope selectors the builder offers, each mapping 1:1 to the
 * engine (LLD 12 §4.1). These are a UI concept layered over the orthogonal
 * `(mode, selector)` model — there is NO new engine mode (D-2).
 *
 *   ALL           → mode = ALL                (every device)
 *   ALLOW_IDS     → mode = ALLOW, ids         (only these devices)
 *   DENY_IDS      → mode = DENY,  ids         (all except these)
 *   ALLOW_MATCH   → mode = ALLOW, predicate   (devices matching…)
 *   DENY_MATCH    → mode = DENY,  predicate   (all except those matching…)
 */
export type DeviceSelector =
  | "ALL"
  | "ALLOW_IDS"
  | "DENY_IDS"
  | "ALLOW_MATCH"
  | "DENY_MATCH";

/** True for the two predicate selectors (which reveal the condition builder). */
export function isPredicateSelector(sel: DeviceSelector): boolean {
  return sel === "ALLOW_MATCH" || sel === "DENY_MATCH";
}

/** The engine `mode` a selector maps to. */
export function selectorMode(sel: DeviceSelector): ScopeMode {
  switch (sel) {
    case "ALL":
      return "ALL";
    case "ALLOW_IDS":
    case "ALLOW_MATCH":
      return "ALLOW";
    case "DENY_IDS":
    case "DENY_MATCH":
      return "DENY";
  }
}

/**
 * Derive the active selector from a loaded `DeviceScope` (edit mode). A present
 * `predicate` wins over `ids` (they are mutually exclusive on the wire); a
 * mode of ALLOW/DENY without a predicate is the ids selector.
 */
export function selectorFromDeviceScope(ds: DeviceScope | undefined): DeviceSelector {
  if (!ds || ds.mode === "ALL") return "ALL";
  if (ds.predicate) {
    return ds.mode === "DENY" ? "DENY_MATCH" : "ALLOW_MATCH";
  }
  return ds.mode === "DENY" ? "DENY_IDS" : "ALLOW_IDS";
}

// Monotonic counter for UI-only condition identity (stable React keys across
// edits/removals). Not security-sensitive; only needs to be unique per session.
let condSeq = 0;
function nextCid(): string {
  condSeq += 1;
  return `c${condSeq}`;
}

/** A fresh, default condition row (default op CONTAINS, case-insensitive). */
export function newCondition(): MatchCondition {
  return { field: "NAME", op: "CONTAINS", value: "", case_insensitive: true, cid: nextCid() };
}

/** Tag a wire-loaded condition (edit mode) with a stable UI id for list keys. */
export function withCid(c: MatchCondition): MatchCondition {
  return { ...c, cid: c.cid ?? nextCid() };
}

/**
 * Build the wire `DeviceScope` from the builder state. Enforces the
 * `ids`-XOR-`predicate` invariant: the ids selector carries `ids` and NO
 * predicate; a predicate selector carries `predicate` and an empty `ids`.
 */
export function buildDeviceScope(
  sel: DeviceSelector,
  ids: string[],
  conditions: MatchCondition[],
  join: MatchJoin,
): DeviceScope {
  const mode = selectorMode(sel);
  if (isPredicateSelector(sel)) {
    return {
      mode,
      ids: [],
      predicate: { join, conditions: conditions.map(normalizeCondition) },
    };
  }
  return { mode, ids: mode === "ALL" ? [] : ids };
}

/** Trim a condition's value for the wire and drop the UI-only `cid`. */
function normalizeCondition(c: MatchCondition): MatchCondition {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { cid, ...wire } = c;
  return { ...wire, value: c.value.trim() };
}

/**
 * Inline over-match warning (LLD 12 §4.3 / D-5): a `CONTAINS` value shorter
 * than {@link CONTAINS_OVERMATCH_MIN_LENGTH} matches very broadly, so warn —
 * but do not block (the default operator stays CONTAINS; Preview is the real
 * guard). Returns true iff this condition should show the warning.
 */
export function isOverMatchContains(c: MatchCondition): boolean {
  if (c.op !== "CONTAINS") return false;
  const v = c.value.trim();
  return v.length > 0 && v.length < CONTAINS_OVERMATCH_MIN_LENGTH;
}

/**
 * Client-side validation of a predicate device scope, mirroring the backend
 * net-new device-scope validation (LLD 12 §4.4). Returns a list of human
 * error strings (empty == valid). Used to gate save and feed the review panel.
 */
export function validatePredicateConditions(
  conditions: MatchCondition[],
): string[] {
  const errors: string[] = [];
  if (conditions.length === 0) {
    errors.push("Add at least one match condition.");
    return errors;
  }
  if (conditions.length > MAX_MATCH_CONDITIONS_PER_AXIS) {
    errors.push(
      `A predicate may have at most ${MAX_MATCH_CONDITIONS_PER_AXIS} conditions.`,
    );
  }
  for (const c of conditions) {
    const v = c.value.trim();
    if (v.length === 0) {
      errors.push("Each condition needs a non-blank value.");
    } else if (v.length > MATCH_VALUE_MAX_LENGTH) {
      errors.push(
        `A condition value may be at most ${MATCH_VALUE_MAX_LENGTH} characters.`,
      );
    }
    if (c.field === "TAG") {
      // Defense-in-depth: the dropdown disables TAG, but reject it anyway so a
      // stale draft can never submit a field the engine rejects until the
      // Devices inventory ships.
      errors.push("Tag matching is not available yet.");
    }
  }
  return errors;
}

/**
 * Whether the rule can be saved given the device-scope state. A predicate
 * selector additionally requires (a) at least one valid condition and (b) the
 * dynamic-set / renamable-name acknowledgement (LLD 12 §4.4). An ids selector
 * requires a non-empty id list within the cap.
 */
export function isDeviceScopeSaveable(
  sel: DeviceSelector,
  ids: string[],
  conditions: MatchCondition[],
  maxIds: number,
  acknowledged: boolean,
): boolean {
  if (sel === "ALL") return true;
  if (isPredicateSelector(sel)) {
    if (!acknowledged) return false;
    return validatePredicateConditions(conditions).length === 0;
  }
  // ids selector
  return ids.length > 0 && ids.length <= maxIds;
}

/**
 * A run of a device name to render, flagged whether it is the matched needle.
 * Used by the preview to highlight (with `<mark>`) the exact substring a
 * condition matched — the same signifier the design mockup uses as the trust
 * anchor for a fragile name match.
 */
export interface NameSegment {
  text: string;
  match: boolean;
}

/**
 * Split a device name into segments, marking the FIRST substring matched by any
 * condition (best-effort, for CONTAINS/PREFIX/SUFFIX/EXACT — GLOB and negated
 * conditions don't highlight a single span). Mirrors the case fold the engine
 * uses (`Locale.ROOT` lowercase when `case_insensitive`). Returns a single
 * unmatched segment when nothing matches, so callers can render uniformly.
 */
export function highlightName(
  name: string,
  conditions: MatchCondition[],
): NameSegment[] {
  for (const c of conditions) {
    const needle = c.value.trim();
    if (!needle || c.op === "GLOB" || c.negate) continue;
    const hay = c.case_insensitive ? name.toLowerCase() : name;
    const ndl = c.case_insensitive ? needle.toLowerCase() : needle;
    let idx = -1;
    if (c.op === "CONTAINS") idx = hay.indexOf(ndl);
    else if (c.op === "PREFIX") idx = hay.startsWith(ndl) ? 0 : -1;
    else if (c.op === "SUFFIX")
      idx = hay.endsWith(ndl) ? hay.length - ndl.length : -1;
    else if (c.op === "EXACT") idx = hay === ndl ? 0 : -1;
    if (idx >= 0 && ndl.length > 0) {
      const end = idx + ndl.length;
      const segs: NameSegment[] = [];
      if (idx > 0) segs.push({ text: name.slice(0, idx), match: false });
      segs.push({ text: name.slice(idx, end), match: true });
      if (end < name.length) segs.push({ text: name.slice(end), match: false });
      return segs;
    }
  }
  return [{ text: name, match: false }];
}

export const MATCH_OPS: { op: MatchOp; label: string }[] = [
  { op: "CONTAINS", label: "contains" },
  { op: "PREFIX", label: "starts with" },
  { op: "SUFFIX", label: "ends with" },
  { op: "EXACT", label: "is exactly" },
  { op: "GLOB", label: "matches (glob * ?)" },
];
